// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — ActionCard
// Individual insight card with "Why?" expandable section
// ══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  Shield, DollarSign, Clock, AlertTriangle, TrendingUp,
  Package, Users, Lightbulb, ChevronDown, ChevronUp,
  Brain, Target, Check, Sparkles
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { Card, Button, Badge } from '@components/ui/Primitives';

const TYPE_ICONS = {
  // Legacy types
  fraud: Shield,
  pricing: DollarSign,
  staffing: Clock,
  waste: AlertTriangle,
  opportunity: TrendingUp,
  inventory: Package,
  customer_pattern: Users,
  // New unified categories
  financial: DollarSign,
  operational: Shield,
  marketing: TrendingUp
};

const SEVERITY_VARIANTS = {
  critical: 'danger',
  warning: 'warning',
  info: 'info'
};

export function ActionCard({ insight, onDismiss, onResolve }) {
  const { theme } = useTheme();
  const { t, lang, isRTL } = useLang();
  const [expanded, setExpanded] = useState(false);

  const Icon = TYPE_ICONS[insight.insight_type] || Lightbulb;

  // Accent color based on type (unified 3 categories + legacy types)
  const accentColor =
    insight.insight_type === 'financial' ? theme.success :
    insight.insight_type === 'operational' ? theme.info :
    insight.insight_type === 'marketing' ? theme.warning :
    // Legacy
    insight.insight_type === 'fraud' || insight.insight_type === 'waste' ? theme.danger :
    insight.insight_type === 'pricing' || insight.insight_type === 'opportunity' ? theme.success :
    insight.insight_type === 'staffing' || insight.insight_type === 'customer_pattern' ? theme.info :
    theme.primary;

  return (
    <Card style={{
      marginBottom: 16,
      overflow: 'hidden',
      [isRTL ? 'borderRight' : 'borderLeft']: `4px solid ${accentColor}`
    }}>
      <div style={{ padding: '22px 24px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', gap: 14, flex: 1, minWidth: 250 }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <Icon size={22} color={accentColor} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 6,
                flexWrap: 'wrap'
              }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
                  {lang === 'ar' ? insight.title_ar : insight.title_en}
                </div>
                <Badge variant={SEVERITY_VARIANTS[insight.severity] || 'info'}>
                  {t[`insightType_${insight.insight_type}`] || insight.insight_type}
                </Badge>
                {insight.is_llm_generated && (
                  <Badge variant="primary">
                    <Sparkles size={10} /> AI
                  </Badge>
                )}
              </div>
              <div style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.7 }}>
                {lang === 'ar' ? insight.description_ar : insight.description_en}
              </div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 8,
            flexShrink: 0
          }}>
            {insight.expected_impact > 0 && (
              <Badge variant={insight.expected_impact > 3000 ? 'success' : 'info'}>
                +{t.sar} {Math.round(insight.expected_impact).toLocaleString()}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => setExpanded(!expanded)}>
              {t.whyThis} {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </Button>
          </div>
        </div>

        {expanded && (
          <div style={{
            marginTop: 16,
            padding: 16,
            background: theme.bgInput,
            borderRadius: 10,
            border: `1px solid ${theme.border}`,
            animation: 'fadeIn 0.2s ease-out'
          }}>
            {/* Why */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Brain size={16} color={theme.primary} />
                <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>
                  {t.whyThis}
                </span>
              </div>
              <p style={{
                margin: 0,
                fontSize: 13,
                color: theme.textSecondary,
                lineHeight: 1.7,
                whiteSpace: 'pre-line'
              }}>
                {lang === 'ar' ? insight.why_ar : insight.why_en}
              </p>
            </div>

            {/* How */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Target size={16} color={theme.success} />
                <span style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>
                  {t.howToFix}
                </span>
              </div>
              <p style={{
                margin: 0,
                fontSize: 13,
                color: theme.textSecondary,
                lineHeight: 1.7,
                whiteSpace: 'pre-line'
              }}>
                {lang === 'ar' ? insight.how_ar : insight.how_en}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={() => onDismiss?.(insight.id)}>
                {t.dismiss}
              </Button>
              <Button size="sm" onClick={() => onResolve?.(insight.id)}>
                <Check size={13} /> {t.markResolved}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
